import React, { useMemo } from 'react'
import { Box, Stack } from '@mui/system'
import { Typography, useTheme, Divider } from '@mui/material'
import NodeCheckbox from '../components/NodeCheckbox'
import NodeToggleIcon from '../components/NodeToggleIcon'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { useTreeContext } from '../../../TreeProvider'
import { getSelectedChildrenCount, useInPerimeter, checkInPerimeter } from '../../../utils'
import { styles, COMMON_WIDTH } from './TreeCommonNode.style'

const getAncestorAtLevel = (node, level) => {
  let current = node.parent

  while (current && current.level >= 0) {
    if (current.level === level) {
      return current
    }

    current = current.parent
  }

  return null
}

const AncestorLine = ({ tree, visible }) => (
  <Stack direction="row" sx={styles.lineStack(tree)}>
    <Stack sx={{ flex: 1 }}>
      <Box sx={{ flex: 1 }} />
    </Stack>
    <Stack sx={{ flex: 1 }}>
      <Box sx={{
        flex: 1,
        ...(visible && { borderLeft: 'solid 1px #EFEFEF' })
      }} />
    </Stack>
  </Stack>
)

const NodeLine = ({ tree, node }) => (
  <Stack direction="row" sx={styles.lineStack(tree)}>
    <Stack sx={{ flex: 1 }}>
      <Box sx={{ flex: 1 }} />
    </Stack>
    <Stack sx={{ flex: 1 }}>
      <Box sx={{
        flex: 1,
        borderLeft: 'solid 1px #EFEFEF',
        ...(!node.nextSibling && { borderBottomLeftRadius: '6px' })
      }} />
      <Box sx={{
        flex: 1,
        ...(!node.nextSibling
          ? {
            borderBottom: 'solid 1px #EFEFEF',
            transform: 'translateY(-16px)',
            borderBottomLeftRadius: '6px',
          }
          : {
            borderLeft: 'solid 1px #EFEFEF'
          }
        )
      }} />
    </Stack>
  </Stack>
)

const TreeCommonNode = ({
  style,
  node,
  tree,
  onlySite,
  displayToggleIcon = false,
  onSelect,
  apiFold,
  disabled
}) => {
  const { waiting, selection, hideCounters } = useTreeContext()
  const { t } = useTranslation()
  const theme = useTheme()
  const checkedAccounts = useInPerimeter()

  const isChecked = useMemo(() => {
    return checkedAccounts.findIndex(account => account.sf_id === node.data.data.sf_id) > -1
  }, [checkedAccounts, node.data.data.sf_id])

  const indeterminate = useMemo(() => {
    const accountsInPerimeter = checkInPerimeter({ accounts: selection })
    if (accountsInPerimeter.findIndex(account => account.sf_id === node.data.data.sf_id && account.in_perimeter) > -1) {
      return false
    }
    return accountsInPerimeter.some(({ hierarchie, sf_id, in_perimeter }) => in_perimeter && sf_id !== node.data.data.sf_id && hierarchie.includes(node.data.data.sf_id))
  }, [selection])

  const displayFolder = useMemo(() => {
    if (!apiFold) return false
    if (!displayToggleIcon) return false
    return node.data.data.nb_children !== node.children.length
  }, [apiFold, displayToggleIcon, node])

  const ancestorLines = useMemo(() => {
    return Array.from({ length: Math.max(node.level - 1, 0) }).map((_, index) => {
      const ancestor = getAncestorAtLevel(node, index + 1)

      return Boolean(ancestor?.nextSibling)
    })
  }, [node])

  const handleCheckBoxChange = async (e, checked) => {
    e.stopPropagation()
    e.preventDefault()
    if (disabled || node.data.disabled) return
    
    if (!node.isOpen) {
      await handleNodeToggle()
    } 
    else if (node.isOpen && isChecked) {
      node.toggle()
    }
    else if (node.isOpen && (!isChecked || indeterminate)) {
      if (displayFolder && apiFold) {
        try {
          await apiFold.toggle(node)
        } catch (error) {
          console.error('Failed to load children:', error)
        }
      }
    }
    
    onSelect?.(node, checked)
  }
  
  const handleNodeToggle = async () => {
    if (!disabled && node.isInternal) {
      node.toggle()
    
      if (displayFolder && apiFold && !node.isOpen && node.children.length <= 1) {
        try {
          await apiFold.toggle(node)
          node.toggle()
        } catch (error) {
          console.error('Failed to load children:', error)
        }
      }
    }    
  }

  const handleCounter = () => {
    if (!node.data.data.should_display_count || hideCounters) return null
    
    const totalSites = node.children.length > 0 
      ? node.children.length 
      : node.data.data.nb_children

    if (totalSites === 0) return null

    const selectedSites = getSelectedChildrenCount(node, checkedAccounts)

    return (
      <Stack direction="row" alignItems="center" sx={styles.counter(theme)}>
        <Typography sx={styles.counterValue}>
          {selectedSites || '-'}
        </Typography>
        <Typography sx={styles.counterTotal(theme)}>
          / {totalSites}
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack
      key={uuidv4()}
      onClick={e => !disabled && handleCheckBoxChange(e, !node.isSelected)}
      style={style}
      sx={styles.container(tree, node)}
      direction="row"
    >
      <Stack direction="row" alignItems="center">
        {ancestorLines.map((visible, index) => (
          <AncestorLine key={index} tree={tree} visible={visible} />
        ))}
        {node.level > 0 && <NodeLine tree={tree} node={node} />}
        <NodeToggleIcon
          sx={{ width: COMMON_WIDTH }}
          open={node.isOpen}
          displayed={displayToggleIcon && node.data.data.nb_children > 0}
          keepSpace={node.level === 0}
          onClick={handleNodeToggle}
          onLoadChildren={async () => displayFolder && apiFold ? await apiFold.toggle(node) : null}
          loading={waiting}
          node={node}
        />
        <NodeCheckbox
          tree={tree}
          node={node}
          checked={isChecked}
          sx={{ width: !onlySite || node.data.data.is_site ? COMMON_WIDTH : 0 }}
          visible={!onlySite || node.data.data.is_site}
          disabled={disabled}
          indeterminate={indeterminate}
          onChange={(e, checked) => !disabled && handleCheckBoxChange(e, checked)}
        />
      </Stack>

      <Stack direction="row" alignItems="center" sx={{ flex: 1, gap: 0.5 }}>
        <Typography sx={styles.nodeText({
          waiting,
          italic: node.data.data.state === 'Etude',
          isParent: node.data.data.nb_children > 0
        })}>
          {node.data.data.highlight_name
            ? <Box dangerouslySetInnerHTML={{ __html: node.data.data.highlight_name }} />
            : node.data.data.name
          }
        </Typography>
        <Box sx={{ flex: 1 }}>
          {node.data.data.nb_children > 0 && !hideCounters && (
            <Divider sx={{ borderColor: '#EFEFEF' }} />
          )}
        </Box>
        {apiFold?.activeNode?.data?.internalId === node.data.internalId && (
          <Typography variant="caption" sx={{ color: theme.palette.grey[300], fontSize: 10 }}>
            {t('actions.arborist_loading')}
          </Typography>
        )}
        {node.data.data.nb_children > 0 && handleCounter()}
      </Stack>
    </Stack>
  )
}

export default TreeCommonNode
